import React, { createContext, useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';

// Discord OAuth2 bilgileri
// ÖNEMLİ: Bu değerleri Discord Developer Portal'dan güncel değerlerle değiştirin!
// 400 hatası genellikle yanlış/süresi geçmiş yetkilendirme bilgilerinden kaynaklanır
const CLIENT_ID = "1357656962195525692"; // Discord Developer Portal'dan güncel ID ile değiştirin
const CLIENT_SECRET = "yU-rWnAe2f6ImaH8Xvwe1LMh3gLj_b7b"; // Discord Developer Portal'dan güncel SECRET ile değiştirin
// Redirect URI'yi sabit bir değer olarak ayarla (Discord Developer Portal'daki ile tam olarak eşleşmeli)
const REDIRECT_URI = "https://kinfhertix.is-a.dev/auth/callback";

// Discord OAuth2 için prompt parametresi ekle - kullanıcı seçimini zorlamak için
const OAUTH_PROMPT = "consent";

// State parametresi için güvenlik önlemi
const generateState = () => {
  const state = Math.random().toString(36).substring(2, 15);
  localStorage.setItem('discord_oauth_state', state);
  return state;
};

// Discord API endpointleri
const DISCORD_API = 'https://discord.com/api/v10';

// Discord sunucu ID'si - kullanıcının yönetici olup olmadığını kontrol etmek için
const DISCORD_GUILD_ID = "1352909282063945799"; // Kinfhertix Discord sunucu ID'si

// Admin rol ID'leri
const ADMIN_ROLE_IDS = ["1352909381003378688"]; // Admin ve moderatör rol ID'leri - bunları kendi rol ID'lerinizle değiştirin

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Sayfa yüklendiğinde local storage'dan kullanıcı bilgilerini kontrol et
    const checkAuth = async () => {
      const userData = localStorage.getItem('discord_user');
      const tokenData = localStorage.getItem('discord_token');
      
      if (userData && tokenData) {
        try {
          const user = JSON.parse(userData);
          const token = JSON.parse(tokenData);
          
          // Token geçerlilik süresini kontrol et
          if (new Date().getTime() < token.expires_at) {
            setCurrentUser(user);
            
            // Kullanıcının sunucudaki rollerini kontrol et
            await checkUserGuildRoles(user.id, token.access_token);
          } else {
            // Token süresi dolmuşsa kullanıcıyı çıkış yaptır
            await logout();
          }
        } catch (error) {
          console.error("Auth check error:", error);
          localStorage.removeItem('discord_user');
          localStorage.removeItem('discord_token');
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Discord'a yönlendirme için URL oluştur
  const getAuthUrl = () => {
    const scope = 'identify guilds guilds.members.read';
    const state = generateState();
    // URL'in parametrelerinin eşleştiğinden emin olmak için encodedRedirect kullan
    const encodedRedirect = encodeURIComponent(REDIRECT_URI);
    
    // Prompt parametresi ekleyerek kullanıcının her defasında seçim yapmasını sağla
    return `${DISCORD_API}/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${encodedRedirect}&response_type=code&scope=${encodeURIComponent(scope)}&state=${state}&prompt=${OAUTH_PROMPT}`;
  };

  // Token almak için Discord API'ye istek at
  const getToken = async (code) => {
    try {
      console.log("Requesting token with code:", code.substring(0, 5) + "...");
      console.log("Using redirect URI:", REDIRECT_URI);
      
      // Token isteği için kullandığımız REDIRECT_URI değeri, authURL'de kullandığımızla tamamen aynı olmalı
      const params = new URLSearchParams();
      params.append('client_id', CLIENT_ID);
      params.append('client_secret', CLIENT_SECRET);
      params.append('grant_type', 'authorization_code');
      params.append('code', code);
      params.append('redirect_uri', REDIRECT_URI); // Tam olarak aynı URI kullanıldığından emin ol
      
      console.log("Full token request params:", params.toString());
      
      const response = await fetch(`${DISCORD_API}/oauth2/token`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error("Discord API error response:", errorData);
        throw new Error(`Discord token request failed: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      console.error("Token request error:", error);
      throw error;
    }
  };

  // Discord API'den kullanıcı bilgilerini al
  const getUser = async (accessToken) => {
    const response = await fetch(`${DISCORD_API}/users/@me`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    
    if (!response.ok) {
      throw new Error(`Discord user request failed: ${response.status}`);
    }
    
    return response.json();
  };

  // Discord API'den kullanıcının sunucularını al
  const getUserGuilds = async (accessToken) => {
    try {
      const response = await fetch(`${DISCORD_API}/users/@me/guilds`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      if (response.status === 429) {
        // Rate limit algılandı
        const retryAfter = response.headers.get('Retry-After') || 5;
        console.warn(`Rate limit detected. Retrying after ${retryAfter} seconds.`);
        
        // Belirtilen süre kadar bekleyip tekrar dene
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        return getUserGuilds(accessToken); // Recursive call after waiting
      }
      
      if (!response.ok) {
        throw new Error(`Discord guilds request failed: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      console.error("Failed to fetch user guilds:", error);
      // Rate limit hatası durumunda boş dizi döndür, böylece işlem devam edebilir
      if (error.message.includes('429')) {
        toast.warning('Discord API hız sınırı aşıldı. Bazı bilgiler görüntülenemeyebilir.');
        return [];
      }
      throw error;
    }
  };

  // Discord API'den kullanıcının sunucudaki üyelik bilgilerini al
  const getGuildMember = async (accessToken, guildId, userId) => {
    try {
      const response = await fetch(`${DISCORD_API}/users/@me/guilds/${guildId}/member`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      if (response.status === 429) {
        // Rate limit algılandı
        const retryAfter = response.headers.get('Retry-After') || 5;
        console.warn(`Rate limit detected. Retrying after ${retryAfter} seconds.`);
        
        // Belirtilen süre kadar bekleyip tekrar dene
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        return getGuildMember(accessToken, guildId, userId); // Recursive call after waiting
      }
      
      if (!response.ok) {
        throw new Error(`Discord guild member request failed: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      console.error("Failed to fetch guild member:", error);
      // Rate limit hatası durumunda boş nesne döndür
      if (error.message.includes('429')) {
        return { roles: [] };
      }
      throw error;
    }
  };

  // Discord callback'ten gelen kod ile token al
  const handleCallback = async (code) => {
    try {
      setLoading(true);
      
      if (!code || typeof code !== 'string' || code.trim() === '') {
        console.error("Invalid authorization code:", code);
        toast.error('Geçersiz yetkilendirme kodu.');
        return false;
      }
      
      console.log("Starting Discord OAuth with code:", code.substring(0, 5) + "...");
      
      // Token al
      const tokenResponse = await getToken(code);
      console.log("Token received successfully");
      
      // Token bilgilerini kaydet
      const expiresAt = new Date().getTime() + tokenResponse.expires_in * 1000;
      const tokenData = {
        ...tokenResponse,
        expires_at: expiresAt
      };
      
      localStorage.setItem('discord_token', JSON.stringify(tokenData));
      
      // Kullanıcı bilgilerini al
      const user = await getUser(tokenResponse.access_token);
      localStorage.setItem('discord_user', JSON.stringify(user));
      
      setCurrentUser(user);
      
      // Kullanıcının sunucudaki rollerini kontrol et
      await checkUserGuildRoles(user.id, tokenResponse.access_token);
      
      toast.success('Discord hesabıyla başarıyla giriş yapıldı!');
      return true;
    } catch (error) {
      console.error("Discord login error:", error);
      toast.error(`Giriş yapılırken bir hata oluştu: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcının sunucudaki rollerini kontrol et
  const checkUserGuildRoles = async (userId, accessToken) => {
    try {
      // Kullanıcının üye olduğu sunucuları al
      const guilds = await getUserGuilds(accessToken);
      
      if (!guilds || guilds.length === 0) {
        console.log("No guilds found or unable to fetch guilds due to rate limiting");
        setIsAdmin(false);
        return;
      }
      
      // Bizim sunucumuzda mı kontrol et
      const ourGuild = guilds.find(guild => guild.id === DISCORD_GUILD_ID);
      
      if (!ourGuild) {
        setIsAdmin(false);
        return;
      }
      
      // Kullanıcı sunucu sahibi mi veya yönetici yetkisi var mı kontrol et
      if (ourGuild.owner || (ourGuild.permissions & 0x8) === 0x8) {
        setIsAdmin(true);
        return;
      }
      
      // Belirli rol ID'lerine göre kontrol et
      try {
        // Kullanıcının sunucudaki member bilgilerini al
        const member = await getGuildMember(accessToken, DISCORD_GUILD_ID, userId);
        
        if (!member || !member.roles) {
          console.log("Could not fetch member roles due to rate limiting");
          setIsAdmin(false);
          return;
        }
        
        // Kullanıcının rolleri arasında admin rolleri var mı kontrol et
        const hasAdminRole = member.roles.some(roleId => ADMIN_ROLE_IDS.includes(roleId));
        
        setIsAdmin(hasAdminRole);
      } catch (memberError) {
        console.error("Member check error:", memberError);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Role check error:", error);
      setIsAdmin(false);
    }
  };

  // Çıkış yap
  const logout = async () => {
    localStorage.removeItem('discord_user');
    localStorage.removeItem('discord_token');
    setCurrentUser(null);
    setIsAdmin(false);
    toast.info('Çıkış yapıldı.');
  };

  const value = {
    currentUser,
    isAdmin,
    loading,
    getAuthUrl,
    handleCallback,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};